import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import type { JSX } from 'react';
import { MainLayout } from '@/components/layout';
import { AdminRoutes } from './AdminRoutes';
import { StudioLayout } from '@/components/Studio/StudioLayout';

// --- Lazy-loaded pages: each page is downloaded only when the user navigates to it ---
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const AdminLoginPage = lazy(() => import('@/pages/AdminLoginPage'));
const BrandDetailsPage = lazy(() => import('@/pages/BrandDetailsPage'));
const BrandOfferDetailPage = lazy(() => import('@/pages/BrandOfferDetailPage'));
const BrandOffersPage = lazy(() => import('@/pages/BrandOffersPage'));
const CategoryPage = lazy(() => import('@/pages/CategoryPage'));
const CreateCurationPage = lazy(() => import('@/pages/CreateCurationPage'));
const CurationsPage = lazy(() => import('@/pages/CurationsPage'));
const CurationsPageV2 = lazy(() => import('@/pages/CurationsPageV2'));
const CuratorStorePage = lazy(() => import('@/pages/CuratorStorePage'));
const HomePage = lazy(() => import('@/pages/HomePage'));
const InternalCreateCurationPage = lazy(() => import('@/pages/InternalCreateCurationPage'));
const OrderPlacedPage = lazy(() => import('@/pages/OrderPlacedPage'));
const PolicyPage = lazy(() => import('@/pages/PolicyPage'));
const ProductDetailPage = lazy(() => import('@/pages/ProductDetailPage'));
const ProductDetailPageV2 = lazy(() => import('@/pages/ProductDetailPageV2'));
const ProductFeedPage = lazy(() => import('@/pages/ProductFeedPage'));
const SearchPage = lazy(() => import('@/pages/SearchPage'));
const SearchSuggestionPage = lazy(() => import('@/pages/SearchSuggestionPage'));
const SelectFrame = lazy(() => import('@/pages/SelectFrame'));
const SimilarProductsPage = lazy(() => import('@/pages/SimilarProductsPage'));
const SlaylistPage = lazy(() => import('@/pages/SlaylistPage'));
const SlaylistView = lazy(() => import('@/pages/SlaylistView'));
const UploadPage = lazy(() => import('@/pages/UploadPage'));
const ApprovalPortalPage = lazy(() => import('@/pages/ApprovalPortalPage'));

export default function AppRoutes(): JSX.Element {
  return (
    // Suspense shows a blank fallback while a lazy page chunk is downloading.
    // You can replace null with a <PageLoader /> spinner component if you have one.
    <Suspense fallback={null}>
      <Routes>
        <Route element={<MainLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/similar-product" element={<SimilarProductsPage />} />
          <Route path="/product/:id" element={<ProductDetailPage />} />
          <Route path="/product/v2/:id" element={<ProductDetailPageV2 />} />
          <Route path="/upload" element={<UploadPage />} />
          <Route path="/curations/:id" element={<CurationsPage />} />
          <Route path="/curations/v2/:id" element={<CurationsPageV2 />} />
          <Route path="/curator-store" element={<CuratorStorePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/privacy-policy" element={<PolicyPage />} />
          <Route path="/search-suggestion" element={<SearchSuggestionPage />} />
          <Route path="/create-curations" element={<CreateCurationPage />} />
          <Route path="/edit-curations/:id" element={<CreateCurationPage />} />
          <Route path="/slaylist">
            <Route index element={<SlaylistPage />} />
            <Route path=":id" element={<SlaylistView />} />
          </Route>
          <Route path="/category/:id" element={<CategoryPage />} />
          <Route
            path="/internal/create-curation"
            element={<InternalCreateCurationPage />}
          />
          <Route path="/internal/feed" element={<ProductFeedPage />} />
          <Route path="/order-placed" element={<OrderPlacedPage />} />
          {/* Add more routes as needed */}
        </Route>
        <Route path="/select-frame" element={<SelectFrame />} />
        <Route path="/slayd-originals/upload" element={<StudioLayout />} />
        <Route path="/studio" element={<StudioLayout />} />
        <Route path="/login" element={<AdminLoginPage />} />
        <Route element={<AdminRoutes />}>
          <Route path="/brand-offers" element={<BrandOffersPage />} />
          <Route path="/brand-offer/:id" element={<BrandOfferDetailPage />} />
          <Route path="/brand-details" element={<BrandDetailsPage />} />
          <Route path="/posts-approve" element={<ApprovalPortalPage />} />
          <Route path="/posts-approve/:id" element={<ApprovalPortalPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
